import { useState, useCallback } from "react";

const STORAGE_KEY = "bookaclassic_recent_searches";
const MAX_RECENT = 5;

export interface RecentSearch {
  location?: string;
  location_country?: string;
  date_from?: string;
  date_to?: string;
  rental_type?: string;
  radius?: number;
  radius_unit?: "km" | "mi";
  timestamp: number;
}

/**
 * Canonical key for a search's location: the leading city token, lowercased
 * and trimmed. This collapses geocoder-suffix variants ("London",
 * "London, England", "London, England, GB", "london") into ONE chip while
 * still distinguishing genuinely different places.
 */
function locationKey(location?: string): string {
  return (location || "").split(",")[0].trim().toLowerCase();
}

/** Normalized comma-separated tokens: "London, England, GB" → ["london","england","gb"]. */
function locationTokens(location?: string): string[] {
  return (location || "")
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Two searches collide when dates + rental type match and one location's
 * token list is a prefix of the other's — geocoder verbosity variants
 * ("London" ⊂ "London, England" ⊂ "London, England, GB") fold together,
 * while contradicting regions ("Paris, FR" vs "Paris, TX") stay apart.
 */
function searchesCollide(a: RecentSearch, b: RecentSearch): boolean {
  if ((a.location_country || "") !== (b.location_country || "")) return false;
  if ((a.date_from || "") !== (b.date_from || "")) return false;
  if ((a.date_to || "") !== (b.date_to || "")) return false;
  if ((a.rental_type || "") !== (b.rental_type || "")) return false;
  const ta = locationTokens(a.location);
  const tb = locationTokens(b.location);
  if (ta.length === 0 || tb.length === 0) return ta.length === tb.length;
  const shorter = ta.length <= tb.length ? ta : tb;
  const longer = ta.length <= tb.length ? tb : ta;
  return shorter.every((tok, i) => tok === longer[i]);
}

/** Keep only the first (most recent) entry per colliding group. */
function dedupeSearches(list: RecentSearch[]): RecentSearch[] {
  const out: RecentSearch[] = [];
  for (const s of list) {
    if (!out.some((kept) => searchesCollide(kept, s))) out.push(s);
  }
  return out;
}

function loadSearches(): RecentSearch[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Dedupe on load too so variants already stored on users' devices
    // collapse without waiting for their next search.
    return dedupeSearches(parsed).slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function saveSearches(searches: RecentSearch[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(searches.slice(0, MAX_RECENT)));
  } catch {
    // localStorage full or unavailable
  }
}

/**
 * Display form of a stored location: the city token only (region/country
 * suffixes add noise in a small chip), title-cased when the user typed it
 * all-lowercase ("london" → "London").
 */
function displayLocation(location: string): string {
  const city = location.split(",")[0].trim();
  if (!city) return location.trim();
  if (city === city.toLowerCase()) {
    return city.replace(/\b\p{L}/gu, (ch) => ch.toUpperCase());
  }
  return city;
}

function buildLabel(s: RecentSearch, fullLocation = false): string {
  const parts: string[] = [];
  if (s.location) {
    const location = fullLocation ? s.location.trim() : displayLocation(s.location);
    parts.push(s.location_country ? `${location} (${s.location_country})` : location);
  }
  if (s.date_from && s.date_to) {
    parts.push(`${s.date_from} – ${s.date_to}`);
  } else if (s.date_from) {
    parts.push(`from ${s.date_from}`);
  } else if (s.date_to) {
    parts.push(`until ${s.date_to}`);
  }
  if (s.rental_type) {
    const labels: Record<string, string> = {
      driving: "Self-Drive",
      chauffeured: "Chauffeur",
      event: "Events",
    };
    parts.push(labels[s.rental_type] || s.rental_type);
  }
  return parts.join(" · ") || "All cars";
}

export function useRecentSearches() {
  const [searches, setSearches] = useState<RecentSearch[]>(loadSearches);

  const addSearch = useCallback(
    (search: Omit<RecentSearch, "timestamp">) => {
      if (!search.location && !search.date_from && !search.date_to && !search.rental_type) {
        return;
      }
      const entry: RecentSearch = { ...search, timestamp: Date.now() };
      const existing = loadSearches();
      const updated = dedupeSearches([entry, ...existing]).slice(0, MAX_RECENT);
      saveSearches(updated);
      setSearches(updated);
    },
    []
  );

  const clearSearches = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    setSearches([]);
  }, []);

  // When two kept searches share a city name (homonyms like "Paris, FR" vs
  // "Paris, TX"), keep the full location in the label so the chips stay
  // distinguishable; otherwise show just the city.
  const cityCounts = new Map<string, number>();
  for (const s of searches) {
    const key = locationKey(s.location);
    if (key) cityCounts.set(key, (cityCounts.get(key) || 0) + 1);
  }
  const labeledSearches = searches.map((s) => ({
    ...s,
    label: buildLabel(s, (cityCounts.get(locationKey(s.location)) || 0) > 1),
  }));

  return { recentSearches: labeledSearches, addSearch, clearSearches };
}
